import { userContext } from './catalog';
import type { SyntheticUser } from './types';
import { USER_VARIABLES } from './variables';

export type UserClaim = {
  path: string;
  token: string;
  original: string;
  value: string;
  faked: boolean;
  type: string;
  description: string;
};

export function formatClaimValue(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map((item) => String(item)).filter(Boolean).join(', ');
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value).trim();
}

export function claimsForUser(user: SyntheticUser | undefined, overrides: Record<string, string> = {}): UserClaim[] {
  if (!user) return [];
  const ctx = userContext(user);
  return USER_VARIABLES.map((item) => {
    const original = formatClaimValue(ctx[item.path]);
    const faked = Object.prototype.hasOwnProperty.call(overrides, item.path);
    return {
      path: item.path,
      token: `\${${item.path}}`,
      original,
      value: faked ? overrides[item.path] : original,
      faked,
      type: item.type,
      description: item.description,
    };
  });
}

export function claimsContext(user: SyntheticUser, overrides: Record<string, string>): Record<string, unknown> {
  const ctx: Record<string, unknown> = { ...userContext(user) };
  Object.entries(overrides).forEach(([path, raw]) => {
    const meta = USER_VARIABLES.find((item) => item.path === path);
    if (meta?.type === 'STRING[]') {
      ctx[path] = raw.split(',').map((part) => part.trim()).filter(Boolean);
      return;
    }
    if (meta?.type === 'BOOLEAN') {
      ctx[path] = /^(true|1|yes)$/i.test(raw.trim());
      return;
    }
    if (meta?.type === 'NUMBER') {
      const numeric = Number(raw);
      ctx[path] = Number.isFinite(numeric) ? numeric : raw;
      return;
    }
    ctx[path] = raw;
  });
  return ctx;
}

export function userOptionLabel(user: SyntheticUser): string {
  const extra = user.department || user.role;
  return extra ? `${user.name} · ${extra}` : user.name;
}

export function identityFromClaims(user: SyntheticUser, claims: UserClaim[]): {
  userId: string;
  userName: string;
  userEmail: string;
  roles: string[];
  groups: string[];
  attributes: Record<string, string>;
  variables: Record<string, string>;
} {
  const variables: Record<string, string> = {};
  claims.forEach((claim) => {
    variables[claim.path] = claim.value;
  });
  const roles = (variables['user.roles'] || user.roles.join(', ')).split(',').map((item) => item.trim()).filter(Boolean);
  const groups = (variables['user.groups'] || user.groups.join(', ')).split(',').map((item) => item.trim()).filter(Boolean);
  return {
    userId: variables['user.id'] || user.id,
    userName: user.name,
    userEmail: variables['user.email'] || user.email,
    roles,
    groups,
    attributes: { clearance: variables['user.attributes.clearance'] || user.clearance },
    variables,
  };
}
